import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SourcingRequest } from "../../lib/api";
import { fmtValue, localeFor } from "../../lib/format";
import { useDecideRequest } from "../../lib/procure";
import { Card, ErrorBox } from "../ui";

const PILL: Record<string, string> = { "Pending approval": "bg-amber-50 text-status-amber", Approved: "bg-green-50 text-status-green", Rejected: "bg-red-50 text-status-red" };

/** The request's approval, before any supplier is asked: the budget against the lines' estimate, the level the budget
 * needs, and the decision — which the person who raised the request can never take. */
export function RequestApproval({ data }: { data: SourcingRequest }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const decide = useDecideRequest();
  const [note, setNote] = useState("");
  const a = data.approval;
  if (!a) return null;
  const fmtDate = (s: string) => (s ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium" }).format(new Date(s.replace(" ", "T"))) : "");
  const why = a.blocked === "own" ? t("p2p.approval.own") : a.blocked ? t("p2p.approval.level", { level: t(`p2p.level.${a.blocked.split(":")[1]}`, a.blocked.split(":")[1]) }) : "";
  return (
    <Card className="px-4 py-3" data-p2p-approval={a.status}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-base font-semibold">{t("p2p.approval.title")} <span className={`ml-2 rounded px-2 py-0.5 text-[11px] font-semibold ${PILL[a.status] ?? ""}`}>{t(`p2p.approval.status.${a.status}`, a.status)}</span></h2>
        <span className="text-xs text-brand-grey">{t("p2p.approval.needs", { level: t(`p2p.level.${a.level}`, a.level) })}</span>
      </div>
      <div className="mt-2 grid gap-2 text-sm sm:grid-cols-3">
        <div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("p2p.budget")}</div><div className="font-semibold">{fmtValue(a.budget_cdf, "CDF", lng)}</div></div>
        <div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("p2p.approval.estimate")}</div><div>{a.estimate_cdf ? fmtValue(a.estimate_cdf, "CDF", lng) : "–"}</div></div>
        <div className="text-xs text-brand-grey">{t("p2p.approval.hint")}</div>
      </div>
      {a.status !== "Pending approval" ? (
        <p className={`mt-2 text-sm ${a.status === "Approved" ? "text-status-green" : "text-status-red"}`} data-p2p-approval-decided>
          ✓ {t("p2p.approval.decided", { decision: t(`p2p.approval.status.${a.status}`, a.status), by: a.decided_by, date: fmtDate(a.decided_on) })}{a.note ? ` — ${a.note}` : ""}
        </p>
      ) : a.can_decide ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("p2p.decisionNote")} className="min-w-64 flex-1 rounded border border-brand-grey-light px-2 py-1" data-p2p-approval-note />
          <button disabled={decide.isPending} onClick={() => decide.mutate({ name: data.name, decision: "Approved", note })} className="rounded bg-status-green px-3 py-1 font-semibold text-white disabled:opacity-50" data-p2p-approval-decide="Approved">{t("p2p.approval.approve")}</button>
          <button disabled={decide.isPending || !note.trim()} onClick={() => decide.mutate({ name: data.name, decision: "Rejected", note })} className="rounded border border-status-red px-3 py-1 text-status-red disabled:opacity-50" data-p2p-approval-decide="Rejected">{t("p2p.reject")}</button>
          {decide.isError && <ErrorBox error={decide.error} />}
        </div>
      ) : <p className="mt-2 text-xs text-brand-grey" data-p2p-approval-waiting>{why || t("p2p.cannotStep")}</p>}
    </Card>
  );
}
